<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Image"
    tabindex="0"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      :style="{ padding: padding }"
      style="word-break: break-word; font-size: 0px"
      :align="align"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-active="isActive || src === ''"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @copy="copyToClipboard"
      ></blockActions>
      <table
        role="presentation"
        cellpadding="0"
        cellspacing="0"
        style="border-collapse: collapse; border-spacing: 0px"
        :class="{
          'w-full': isFluid,
        }"
        :align="align"
        border="0"
      >
        <image-upload
          ref="imgUpload"
          :path="'/'"
          :options="options"
          @fileUploaded="fileUploaded"
          @fileUploadError="onFileUploadError"
          @notify="notify"
        ></image-upload>
        <tbody>
          <tr v-if="isVideo">
            <td :style="{ width: widthWithFluidOnMobile }">
              <a
                v-if="src !== '' && src && isValidSrc"
                @click.stop="clickImage"
              >
                <img
                  :id="id"
                  :alt="alt"
                  :title="title"
                  height="auto"
                  :src="src"
                  :style="imgStyle"
                  :width="width.replace('px', '')"
                />
              </a>
              <div v-else class="placeholder-container">
                <div class="img-text-container">
                  <svg
                    class="img-1"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="30"
                      height="30"
                      rx="15"
                      stroke="#6DA8FF"
                      stroke-width="2"
                    />
                    <path d="M12 23V9L24 16.0002L12 23Z" fill="#6DA8FF" />
                  </svg>

                  <svg
                    class="img-2"
                    width="32"
                    height="32"
                    viewBox="0 0 36 36"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="2"
                      y="2"
                      width="32"
                      height="32"
                      rx="16"
                      fill="#6DA8FF"
                    />
                    <rect
                      x="1"
                      y="1"
                      width="34"
                      height="34"
                      rx="17"
                      stroke="white"
                      stroke-opacity="0.4"
                      stroke-width="2"
                      stroke-miterlimit="2.61313"
                      stroke-linejoin="round"
                    />
                    <path d="M14 25V11L26 18.0002L14 25Z" fill="white" />
                  </svg>

                  <div class="img-text">
                    {{ $t("mjml.add_video") }}
                  </div>
                </div>
              </div>
            </td>
          </tr>
          <tr v-else :style="hiddenStyle">
            <td
              :style="{ width: widthWithFluidOnMobile }"
              @dragover.prevent
              @drop="onDrop"
            >
              <a v-if="src && isValidSrc" @click.stop="clickImage">
                <img
                  :id="id"
                  :alt="alt"
                  :title="title"
                  :width="width.replace('px', '').replace('%', '')"
                  height="auto"
                  :src="src"
                  :style="imgStyle"
                />
              </a>

              <img
                v-else-if="!isValidSrc && src && containMergetags"
                :src="`${awsFolder}/assets/images/merge-tags/dummy.svg`"
                class="max-h-[300px] bg-[#D4D5D9] object-scale-down"
                :style="imgStyle"
              />

              <img
                v-else-if="
                  !isValidSrc &&
                  src &&
                  !fileUploading &&
                  !evaluatingImgExistance
                "
                alt="Image Not Found Error"
                src="https://topolio.s3.eu-west-1.amazonaws.com/editor/error.png"
                :style="imgStyle"
              />

              <div v-else>
                <button
                  v-if="!fileUploading"
                  :id="id"
                  class="placeholder-container"
                  :class="{ 'placeholder-container-hovered': isDraggedOver }"
                  :width="width"
                  @click.stop="clickImage"
                  @dragleave.stop="onDragLeave"
                  @dragstart.stop="onDragOver"
                  @dragenter.stop="onDragOver"
                >
                  <div class="img-text-container">
                    <svg
                      class="img-1"
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="1"
                        y="1"
                        width="30"
                        height="30"
                        rx="5"
                        stroke="#6DA8FF"
                        stroke-width="2"
                      />
                      <path
                        d="M7.74352 7C9.25874 7 10.487 8.20881 10.487 9.7C10.487 11.1912 9.30961 12.4 7.74352 12.4C6.17743 12.4 5 11.1912 5 9.7C5 8.20881 6.27917 7 7.74352 7ZM26.891 24.5275C26.731 24.82 26.4223 25 26.0851 25H6.08083C5.73669 25 5.42182 24.8099 5.26578 24.5078C5.10974 24.2059 5.13918 23.8435 5.34203 23.5696L9.34299 18.1696C9.51538 17.935 9.78973 17.8 10.0812 17.8C10.3727 17.8 10.648 17.9373 10.8203 18.1698L12.6756 20.6735L18.0083 12.8491C18.1803 12.5519 18.4661 12.4 18.769 12.4C19.072 12.4 19.3606 12.5503 19.5298 12.8008L26.8458 23.6008C27.0339 23.875 27.051 24.2294 26.891 24.5275Z"
                        fill="#6DA8FF"
                      />
                    </svg>

                    <svg
                      class="img-2"
                      width="32"
                      height="32"
                      viewBox="0 0 36 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="2"
                        y="2"
                        width="32"
                        height="32"
                        rx="6"
                        fill="#6DA8FF"
                      />
                      <rect
                        x="1"
                        y="1"
                        width="34"
                        height="34"
                        rx="7"
                        stroke="white"
                        stroke-opacity="0.4"
                        stroke-width="2"
                      />
                      <path
                        d="M9.74352 9C11.2587 9 12.487 10.2088 12.487 11.7C12.487 13.1912 11.3096 14.4 9.74352 14.4C8.17743 14.4 7 13.1912 7 11.7C7 10.2088 8.27917 9 9.74352 9ZM28.891 26.5275C28.731 26.82 28.4223 27 28.0851 27H8.08083C7.73669 27 7.42182 26.8099 7.26578 26.5078C7.10974 26.2059 7.13918 25.8435 7.34203 25.5696L11.343 20.1696C11.5154 19.935 11.7897 19.8 12.0812 19.8C12.3727 19.8 12.648 19.9373 12.8203 20.1698L14.6756 22.6735L20.0083 14.8491C20.1803 14.5519 20.4661 14.4 20.769 14.4C21.072 14.4 21.3606 14.5503 21.5298 14.8008L28.8458 25.6008C29.0339 25.875 29.051 26.2294 28.891 26.5275Z"
                        fill="white"
                      />
                    </svg>

                    <div
                      class="img-text"
                      :style="{ paddingBottom: padBot + 'px' }"
                    >
                      {{ $t("layout.drop_your_image_here") }}
                    </div>
                  </div>
                </button>
                <div v-else class="light-theme image-loading-container">
                  <TLoadingSpinner large></TLoadingSpinner>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockActions from "./blockActions.vue";
  import { TopolImageAndVideoBlock } from "@/schemas/template/blocks/imageAndVideoBlockSchema";

  import superEncodeURI from "@/utils/superEncodeURI";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useFileManagerStore } from "@/stores/fileManger";
  import { useNotificationsStore } from "@/stores/notifications";
  import blockProperties from "@/composables/useBlockProperties";

  import { formatBytes } from "@topol/utils";
  import { TLoadingSpinner } from "@topol/twig-ui";
  import {
    ImageUpload,
    extractImageFilesFromClipboard,
  } from "@topol/image-picker";
  import type { FileOptions } from "@topol/image-picker";

  import { useI18n } from "vue-i18n";
  import { useDebounceFn } from "@vueuse/core";
  import {
    StyleValue,
    computed,
    ref,
    watch,
    onMounted,
    onBeforeUnmount,
  } from "vue";

  const mjmlStore = useMjmlStore();
  const mergeTagsStore = useMergeTagsStore();
  const fileManagerStore = useFileManagerStore();
  const notificationsStore = useNotificationsStore();
  const { t } = useI18n();

  const props = defineProps<{
    block: TopolImageAndVideoBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    blockHeight,
    blockWidth,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const isValidSrc = ref(true);
  const fileUploading = ref(false);
  const isDraggedOver = ref(false);
  const evaluatingImgExistance = ref(false);
  const imgUpload = ref();
  const containMergetags = ref(false);

  const options = ref<FileOptions>({
    ...fileManagerStore.FMOptions,
    imageCreation: true,
  });

  const awsFolder = computed(() => {
    return import.meta.env.VITE_APP_AWS_FOLDER;
  });

  const imgStyle = computed<StyleValue>(() => {
    return {
      border: "none",
      display: "block",
      outline: "none",
      "text-decoration": "none",
      height: "auto",
      "max-width": "100%",
      width: widthWithFluidOnMobile.value,
      "border-radius": props.block.attributes["border-radius"] || 0,
    };
  });

  const align = computed(() => {
    return props.block.attributes["align"]
      ? props.block.attributes["align"]
      : "center";
  });

  const isFluid = computed(() => {
    return (
      mjmlStore.isMobileFirstEnabled &&
      mjmlStore.isMobileView &&
      props.block.attributes["fluid-on-mobile"] === "true"
    );
  });

  const widthWithFluidOnMobile = computed(() => {
    if (isFluid.value) {
      return "100%";
    }

    return width.value;
  });

  const width = computed(() => {
    if (
      !isValidSrc.value &&
      src.value &&
      !evaluatingImgExistance.value &&
      !containMergetags.value
    ) {
      return "50px";
    }

    if (widthNumber.value === "100%") {
      return widthNumber.value;
    } else {
      return Math.max(widthNumber.value, 0) + "px";
    }
  });

  const widthNumber = computed(() => {
    if (
      props.block.attributes["width"] &&
      parseInt(props.block.attributes["width"].toString()) <=
        props.block.attributes["containerWidth"]
    ) {
      return parseInt(props.block.attributes["width"].toString());
    }
    // Calculate width
    // Get padding from sides
    const paddingParts = padding.value.split(" ");
    if (paddingParts.length == 1) {
      return (
        props.block.attributes["containerWidth"] - 2 * parseInt(paddingParts[0])
      );
    } else if (paddingParts.length == 2) {
      return (
        props.block.attributes["containerWidth"] - parseInt(paddingParts[1])
      );
    } else if (paddingParts.length == 4) {
      return (
        props.block.attributes["containerWidth"] -
        parseInt(paddingParts[1]) -
        parseInt(paddingParts[3])
      );
    }
    return "100%";
  });

  const padding = computed(() => {
    if (props.block.attributes["padding"]) {
      return props.block.attributes["padding"];
    }
    return "10px 25px";
  });

  const src = computed(() => {
    const srcString =
      mjmlStore.getSpecificBlockAttr({
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
        attr: "src",
      }) || "";
    testContainsMergetag(srcString);
    return srcString;
  });

  const isVideo = computed(() => {
    return props.block.isVideo || false;
  });

  const alt = computed(() => {
    return mjmlStore.getSpecificBlockAttr({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
      attr: "alt",
    });
  });

  const title = computed(() => {
    return mjmlStore.getSpecificBlockAttr({
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
      attr: "title",
    });
  });

  const padBot = computed(() => {
    if (widthNumber.value === "100%") {
      return 0;
    } else if (widthNumber.value >= 300) {
      return 24;
    } else if (widthNumber.value >= 200) {
      return 16;
    } else if (widthNumber.value >= 150) {
      return 4;
    } else {
      return 0;
    }
  });

  watch(src, (newImageUrl, oldImageUrl) => {
    if (newImageUrl !== oldImageUrl) {
      evaluatingImgExistance.value = true;
      debouncedImageExists(newImageUrl);
    }
  });

  function clickImage() {
    click();

    setTimeout(() => {
      const chooseButton = document.querySelector(
        '[data-testid="section-choose-image"]'
      );
      if (!src.value && chooseButton) {
        //@ts-expect-error
        chooseButton.click();
      }
    }, 100);
  }

  async function onDrop(e: any) {
    if (isDragSourceExternalFile(e.dataTransfer)) {
      e.preventDefault();

      click();

      if (e.dataTransfer.files.length > 0) {
        fileUploading.value = true;
        imgUpload.value.uploadFile(e.dataTransfer.files[0]);
      }
    }
  }

  function isEditableTarget(el: Element | null): boolean {
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return true;
    return (el as HTMLElement).isContentEditable;
  }

  function onPaste(e: ClipboardEvent) {
    if (
      !isActive.value ||
      isVideo.value ||
      fileManagerStore.visibleFileManager ||
      isEditableTarget(document.activeElement)
    ) {
      return;
    }

    const files = extractImageFilesFromClipboard(e);
    if (files.length === 0) return;

    e.preventDefault();
    click();
    fileUploading.value = true;
    imgUpload.value.uploadFile(files[0]);
  }

  function onFileUploadError() {
    fileUploading.value = false;
  }

  function fileUploaded(file: any) {
    if (file) {
      const res = JSON.parse(file.xhr.response);

      mjmlStore.updateBlockAttribute({
        prop: "src",
        value: superEncodeURI(res.url),
      });
    }
    fileUploading.value = false;
  }

  function onDragOver(e: any) {
    if (isDragSourceExternalFile(e.dataTransfer)) {
      e.stopPropagation();
      isDraggedOver.value = true;
    }
  }

  function onDragLeave() {
    isDraggedOver.value = false;
  }

  // Detect if this is externally dragged file
  function isDragSourceExternalFile(dataTransfer: any) {
    // Source detection for Safari v5.1.7 on Windows.
    if (typeof Clipboard != "undefined") {
      // eslint-disable-next-line no-undef
      if (dataTransfer.constructor == Clipboard) {
        //@ts-expect-error
        if (dataTransfer.files.length > 0) return true;
        else return false;
      }
    }

    // Source detection for Firefox on Windows.
    if (typeof DOMStringList != "undefined") {
      const dragDataType = dataTransfer.types;
      if (dragDataType.constructor == DOMStringList) {
        if (dragDataType.contains("Files")) return true;
        else return false;
      }
    }

    // Source detection for Chrome on Windows.
    if (typeof Array != "undefined") {
      const dragDataType = dataTransfer.types;
      if (dragDataType.constructor == Array) {
        if (dragDataType.indexOf("Files") != -1) return true;
        else return false;
      }
    }
  }

  function imageExists(imageUrl: string) {
    return new Promise(() => {
      const image = new Image();

      image.onload = () => {
        if (image.width === 0) {
          isValidSrc.value = false;
        } else {
          isValidSrc.value = true;
        }
        evaluatingImgExistance.value = false;
      };

      image.onerror = () => {
        isValidSrc.value = false;
        evaluatingImgExistance.value = false;
      };

      image.src = imageUrl;
    });
  }

  function testContainsMergetag(str: string) {
    const syntax = mergeTagsStore.smartMergeTags?.syntax || {
      start: "*|",
      end: "|*",
    };
    const merge_tags_syntax_start = syntax.start;
    const merge_tags_syntax_end = syntax.end;

    const merge_tags_syntax_start_escaped =
      "\\" + merge_tags_syntax_start.split("").join("\\");
    const merge_tags_syntax_end_escaped =
      "\\" + merge_tags_syntax_end.split("").join("\\");

    const mergeTagScannerRegex =
      merge_tags_syntax_start_escaped +
      "([a-zA-Z_0-9\\.\\[\\]]+)(\\?[^\\|]+)?" +
      merge_tags_syntax_end_escaped;
    const regex = new RegExp(mergeTagScannerRegex);
    const match = str.match(regex);
    if (!match || match.length === 0) {
      containMergetags.value = false;
    } else {
      containMergetags.value = true;
    }
  }

  const notificationsKeys = [
    {
      name: "invalid_folder_name",
      key: "file_manager.folder_name_invalid",
    },
    {
      name: "uploading_images_failed",
      text: "Uploading images failed",
    },
    {
      name: "selecting_images_failed",
      text: "Selecting images failed",
    },
    {
      name: "extension_error",
      key: "file_manager.extension_error",
    },
  ];

  function notify(name: string) {
    if (name.startsWith("upload_error:")) {
      notificationsStore.createNotification({
        text: t("file_manager.upload_error", {
          msg: name.slice("upload_error:".length),
        }),
        type: "error",
      });
      return;
    }

    if (name.startsWith("file_already_exists:")) {
      notificationsStore.createNotification({
        text: t("file_manager.file_name_exists", {
          msg: name.split(":")[1],
        }),
        type: "error",
      });
      return;
    }

    if (name.startsWith("folder_name_exists:")) {
      notificationsStore.createNotification({
        text: t("file_manager.folder_name_exists", {
          msg: name.split(":")[1],
        }),
        type: "error",
      });
      return;
    }

    if (name === "max_size_exceeded") {
      notificationsStore.createNotification({
        text: t("file_manager.max_size_exceeded", {
          size: formatBytes(fileManagerStore.imageMaxSize),
        }),
        type: "error",
      });
      return;
    }

    if (name.startsWith("limit-exceeded:")) {
      notificationsStore.createNotification({
        text: t("file_manager.max_uploading_files_exceeded", {
          count: fileManagerStore.preferences?.maxUploadingFiles,
          total: name.split(":")[1],
        }),
        type: "error",
      });
      return;
    }

    const thisMessage = notificationsKeys.find(
      (message) => message.name === name
    );
    if (!thisMessage) {
      return;
    }
    if (thisMessage.key) {
      notificationsStore.createNotification({
        text: t(thisMessage.key),
        type: "error",
      });
      return;
    }
    if (thisMessage.text) {
      notificationsStore.createNotification({
        text: thisMessage.text,
        type: "error",
      });
      return;
    }
  }

  const debouncedImageExists = useDebounceFn(async function (newV) {
    imageExists(newV);
  }, 300);

  onMounted(() => {
    imageExists(src.value);
    document.addEventListener("paste", onPaste);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("paste", onPaste);
  });
</script>

<style lang="scss" scoped>
  .placeholder-container {
    color: rgb(109, 168, 255);
    background-color: rgb(231, 243, 255);
    text-decoration: none;
    text-align: center;
    line-height: 18px;
    display: flex;
    position: relative;
    justify-content: center;
    cursor: pointer;
    // border-radius: 6px;
    border: none;
    outline: #6da8ff4d 1px solid;

    width: calc(100%);
    height: 140px;
    display: flex;
    justify-content: center;
    font: var(
      --chip-font,
      var(--chip-font-weight, var(--font-normal))
        var(--chip-font-size, var(--font-xs)) / var(--chip-font-line-height, 1)
        var(--chip-font-family, var(--font-family-primary))
    );

    .img-text-container {
      margin-top: 38px;
      pointer-events: none;

      .img-1 {
        padding-bottom: 12px;
        display: inline-block;
      }

      .img-2 {
        padding-bottom: 12px;
        display: none;
      }
    }
  }

  .placeholder-container:hover,
  .placeholder-container-hovered {
    background-color: rgb(206, 231, 255) !important;
    color: rgb(72, 131, 255) !important;
    border-radius: 0px;
    .img-text-container {
      .img-1 {
        padding-bottom: 12px;
        display: none;
      }

      .img-2 {
        padding-bottom: 12px;
        display: inline-block;
      }
    }
  }
  .image-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 140px;
    background-color: rgb(231, 243, 255);
    border: rgba(109, 168, 255, 0.3) 1px solid;
  }
</style>
